/**
 * Exact merged Engineered To Order component.
 *
 * This file merges the logic of:
 *  - EngineeredToOrderLineItemView (parent wrapper)
 *  - WebModuleFranchiseeETOEditor (child editor)
 *
 * Rules applied:
 *  - All child (editor) instance members / methods are copied and renamed with prefix  where collisions could occur.
 *  - References in former parent that used `this.etoEditor` now call the corresponding  member directly.
 *  - No functional or behavioral changes; code blocks copied verbatim except for namespacing/renaming.
 *  - Rendering inlined: parent bodyTemplate now renders what child render() provided directly.
 *  - Original files remain untouched.
 */
import { addV6DebugInformation, clearAllV6DebugInformation } from '../../../quotes/v6/v6-debug-information';
import {
  applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections,
  assetContentTag,
  convertV6CommitDetails,
  fetchQuoteAsset,
  getV6FrameForContainer,
  getV6QuoteItemForContainer,
  setProviderData
} from '../../../quotes/data/v6/helper-functions';
import { AskConfirmation, buttonsContinueCancel } from '../../../../webmodule-common/other/ui/modal-confirmation';
import { buildThumbnailAsset } from '../../../quotes/data/quote-item-thumbnail-asset';
import {
  AttachedBlobAsset,
  InputUpdateQuoteItems,
  QuoteItemState,
  QuoteItemType
} from '../../../api/dealer-api-interface-quote';

import {
  base64DataStringToRawBase64,
  containsFlag,
  firstValidString,
  flagEquals,
  isEmptyOrSpace
} from '../../../../webmodule-common/other/ui/string-helper-functions';
import {
  BranchQuoteSupport,
  BranchQuoteSupportItemType,
  BranchQuoteSupportStatus,
  QuoteItemConversation,
  QuoteItemConversationType,
  ViewConversationEntry
} from '../../../api/dealer-api-interface-franchisee';
import {
  compareFrames,
  FrameCompareReport,
  FrameV2,
  FrameValidationType,
  getFrameCompareAsMarkdown,
  QuoteFrameDataProvider,
  v6FrameUserMode,
  V6QuoteFrameEditor,
  V6QuoteFrameEditorData
} from '../../../../webmodule-common/interop/webmodule-interop';
import { customElement, query, state } from 'lit/decorators.js';
import { DevelopmentError } from '../../../../webmodule-common/other/development-error';
import { emptyGuid, newGuid } from '../../../../webmodule-common/other/api/guid';
import { EventTemplate, Snippet } from '../../../../webmodule-common/other/ui/events';
import { FranchiseeQuoteContainerManager } from '../data/franchisee-quote-manager';
import { getApiFactory } from '../../../api/api-injector';
import { getCurrentUser } from '../../../../webmodule-common/other/api/current-user';
import {
  getQuoteItemTab,
  handleCommentEdit,
  isSpecialItem,
  isSSI,
  isSupplierFrameItem,
  isSupplierSpecialItem
} from '../../../quotes/data/quote-helper-functions';
import { createContentPreview } from '../../../../webmodule-common/other/ui/templateresult/preview-helpers';
import { getQuoteSuppliers } from '../../../quotes/quote-service';
import { html, nothing, TemplateResult } from 'lit';
import { information } from '../../../../webmodule-common/other/ui/modal-option';
import { LangFranchiseeEngineeredToOrder } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-engineered-to-order-lang';
import { LangV6QuoteItemFrameView } from '../../../../localization/scripts/quotes/v6/v6-quote-item-frame-view-lang';
import { LineItemView } from '../../../quotes/views/line-item-view';
import { lockUIandExecute } from '../../../../webmodule-common/other/ui-lock';
import { nowServer } from '../../../../webmodule-common/other/datetime-converter';
import { offline } from '../../../../webmodule-common/other/ui/offline';
import { allItemIds, findGroup, getGroups, getItemGroupId } from '../../../quotes/data/quote-item-grouping';
import { isDeletedQuoteItemState } from '../../../quotes/data/quote-container';
import { quoteItemContentType } from '../../../quotes/data/default-quote-item-content-type';
import type { FormInputSelectValue } from '../../../../webmodule-common/other/ui/templateresult/form-input-types';
import { quoteSupplierProvider } from '../../../quotes/data/quoteSupplierProvider';
import { toJsonStr } from '../../../../webmodule-common/other/blob-converters';
import { userSecurity } from '../../../../webmodule-common/other/api/user-security';
import { v6BootLoaderWithFailMsg } from '../../../v6config/v6config';
import { V6CustomItemRequestType, v6Editors, v6Util } from '../../../../webmodule-common/interop/interfaces/v6-config';
import { V6FranchiseeQuoteProviderData } from '../data/franchisee-quote-provider-data';

import type { QuoteItemViewOptions } from '../../../quotes/views/quote-item-view';

import { getSupportStatusBadge } from '../../conversation/conversation-helper';
// Removed SAVED_TOAST_REASONS import – subscription now mirrors ticket editor without toast logic
import { DataBinding } from '../../../../webmodule-common/other/ui/databinding/databinding';
import {
  DataTracker,
  DynamicValueBinder,
  FieldType
} from '../../../../webmodule-common/other/ui/databinding/data-tracker';
import { deriveSupportCapabilities, renderSupportDebugBanner } from '../../conversation/support-capabilities';
import { fireQuickInformationToast } from '../../../../webmodule-common/other/toast-away';
import { FormInputAssistant } from '../../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { FormInputAssistantBase } from '../../../../webmodule-common/other/ui/templateresult/form-input-assistant-base';
import { InformationDispatcher } from '../../../../webmodule-common/other/ui/information-dispatcher';
import { money } from '../../../../webmodule-common/other/currency-formatter';
import {
  MutableSupportTicketBeforeCreateContext,
  MutableSupportTicketBeforeEditContext,
  SupportTicketCreateContext,
  SupportTicketPostEditContext,
  SupportTicketService
} from '../../conversation/support-ticket-service';
import { SaveWorkflowModal } from '../../../../webmodule-common/other/save-workflow';
import { stringsAsMarkdownList } from '../../../../webmodule-common/other/general/markdown';
import { updateFreehandPrice } from '../data/v6/freehand-pricing';
import type {
  EventValueGetter,
  EventValueSetter
} from '../../../../webmodule-common/other/ui/databinding/data-tracker';
// Removed missing mixin import (SupportTicketServiceProvider already satisfied via implemented interface)
import { currentUserClaims, supplierHasFullPrivelages } from '../../../../webmodule-common/other/currentuser-claims';
import { LangFranchiseeQuoteManager } from '../../../../localization/scripts/dealer-franchisee/quotes/data/franchisee-quote-manager-lang';
import { sleep } from '../../../../webmodule-common/other/general/time';

import '../../conversation/wm-support-conversation-entry-editor';

import '../../conversation/support-ticket-actions';
import '../../conversation/wm-conversation-view';
import '../../conversation/wm-eto-masterdocument-uploader';
import { LangBranchQuoteSupportNew } from '../../../../localization/scripts/dealer-franchisee/conversation/wm-branchquote-support-new-lang';
import type { FormInputImageFileUploadView } from '../../../../webmodule-common/other/ui/templateresult/FormInputView';

import { newStandardEditor } from '../../../../webmodule-common/other/ui/toastui';
import { showValidations } from '../../../../webmodule-common/other/ui/modal-validationhandler';
import { waitPatientlySaving } from '../../../../webmodule-common/other/ui/modal-saving';
import { WebModuleConversationEntryDropZone } from '../../conversation/wm-conversation-entry-dropzone';
import type { WMEtoMasterDocumentUploader } from '../../conversation/wm-eto-masterdocument-uploader';

// Removed unused types from merged context (editor inlined)

@customElement('wm-engineeredtoorderlineitemview-merged')
export class EngineeredToOrderLineItemViewMerged extends LineItemView implements QuoteFrameDataProvider {
  // ---------------- Parent state ----------------
  @state() protected branchQuoteSupport?: BranchQuoteSupport;

  protected franchiseeApi = getApiFactory().franchisee();
  @state() protected quoteItemConversationLink?: QuoteItemConversation | null;
  protected dataPrimaryKey = newGuid();
  protected masterDocumentConversationId: string = newGuid();
  // Parity: original view maintained a transient id fallback via _id; reproduce for compatibility with any upstream consumers.
  private _id: string = newGuid();
  supplierSelector?: V6CustomItemRequestType; // imported below
  @state() v6Editor: V6QuoteFrameEditor | null = null;
  supplierOffline: boolean = false;
  private _isNewItem: boolean;
  // Session-centric draft ticket id (null until created via createSupportTicketDraft)
  private _draftTicketId: string | null = null;
  protected assets: { [key: string]: AttachedBlobAsset } = {};
  /**
   * Pending group assignment chosen in the properties panel, applied as part of the editor's save
   * flow (not immediately). `undefined` means the user has not touched the selector; otherwise it
   * holds the chosen target group id, or `null` for the trunk (ungrouped).
   */
  private _pendingGroupId: string | null | undefined = undefined;
  /**
   * When set, the user typed a brand-new group name in the combobox ("Create ..."). On save the
   * group is created and the item moved into it. Takes precedence over `_pendingGroupId`.
   */
  private _pendingNewGroupName: string | null = null;

  // ---------------- Child (editor) migrated members (prefixed ) ----------------

  protected isNew(): boolean {
    return this._isNewItem; // !this.quoteItemContainer || isEmptyOrSpace(this.quoteItemContainer.item.recordVersion);
  }
  protected originalFrameData?: FrameV2 | null;
  protected originalsupplierGrossSingleUnitCost?: number;
  protected originalQty?: number;
  protected originalThumbnail?: string;
  protected dataBinding = new DataBinding(this);
  protected dataTracker = new DataTracker(this.dataBinding);
  protected conversationEntryId: string = newGuid();

  protected claims = currentUserClaims();
  // Conversation state now fully sourced from SupportTicketService snapshots to avoid divergence.
  // Use computed getters instead of local mutable arrays/ids.
  protected get entries(): ViewConversationEntry[] {
    const id = this.currentTicketId;
    if (!id) return []; // Ensure we return an empty array if no ID is present
    try {
      const svc = this.qcm.supportTicketService;
      const snap: any = svc.getState().tickets?.[id];
      return snap?.entries ?? [];
    } catch (err) {
      this.logWarn('entries-get', err);
      return [];
    }
  }

  public async fetchAsset(assetType: string): Promise<string | object | null> {
    if (!this.quoteItemContainer) return null;
    const newPath = this.quoteItemContainer.data?.assetVirtualPaths?.[assetType]?.path ?? '';
    const publicUrl = this.quoteItemContainer.data?.assetVirtualPaths?.[assetType]?.publicUrl ?? null;
    const v6Data = getV6QuoteItemForContainer(this.quoteItemContainer) as any;

    switch (assetType) {
      case 'model3d':
        return (
          v6Data.vrml ??
          (await fetchQuoteAsset({
            quoteId: this.quoteItemContainer.item.quoteId,
            quoteItemId: this.quoteItemContainer.item.id,
            assetPath: newPath,
            publicUrl: publicUrl
          }))
        ); // Handle 3D model asset
        break;
      case 'bomView':
        v6Data.bomView ??
          (await fetchQuoteAsset({
            quoteId: this.quoteItemContainer.item.quoteId,
            quoteItemId: this.quoteItemContainer.item.id,
            assetPath: newPath,
            publicUrl: publicUrl
          }));
        break;
      case 'thumbnail':
        return null; //notsupported
        break;
      case 'data':
        return v6Data;
        break;
      default:
        return null;
    }
    return null;
  }

  protected get editingEntryId(): string | null {
    const id = this.currentTicketId;
    if (!id) return null;
    try {
      const svc = this.qcm.supportTicketService;
      return svc.getEditingEntryId(id) ?? null;
    } catch (err) {
      this.logWarn('editingEntryId-get', err);
      return null;
    }
  }
  isFrameBasedRequest?: boolean = false;

  // supportUnsub removed (subscription now uses _unsubscribe like ticket editor)
  private updatingFromSnapshot = false;
  // Removed fireSavedToast (no longer used after subscription parity change)

  // Editors (from original child component) - strongly typed queries
  @query('#masterdocument') protected masterDocumentEditor?: WMEtoMasterDocumentUploader;
  @query('#thumbnail') protected thumbNailEditor?: FormInputImageFileUploadView;

  // Information dispatcher instance used during save workflow
  private _informationDispatcher: InformationDispatcher | null = null;
  // Removed redundant franchiseeQuoteManager (use this.qcm instead)

  constructor(options: QuoteItemViewOptions) {
    super(options);
    // Child binding setup
    this.prepareBindings();
  }

  // --- SupportTicketServiceProvider implementation ---
  public get supportTicketService(): SupportTicketService {
    if (!this.quoteManager) throw new DevelopmentError('missing quote manager');
    return (this.quoteManager as FranchiseeQuoteContainerManager).supportTicketService;
  }

  // ------------- Mapping getters to child-prefixed equivalents -------------
  //TODO; examine backupPriceDetails usage
  get ticketClosed() {
    const status = this.currentTicketStatus;
    if (!status) return false; // new draft not yet persisted
    return containsFlag(status, BranchQuoteSupportStatus.Cancelled, BranchQuoteSupportStatus.Resolved);
  }

  // Unified ticket id: new draft uses draft ticket id from service, existing uses branchQuoteSupport.id
  private get currentTicketId(): string | null {
    return this.dataPrimaryKey;
  }

  // Status resolution: prefer service snapshot; fallback to local object
  private get currentTicketStatus(): BranchQuoteSupportStatus | null {
    try {
      const svc = this.qcm?.supportTicketService;
      const id = this.currentTicketId;
      if (svc && id) {
        const snap: any = svc.getState();
        const ticketSnap = snap?.tickets?.[id];
        if (ticketSnap?.ticket?.status !== undefined && ticketSnap.ticket.status !== null)
          return ticketSnap.ticket.status as BranchQuoteSupportStatus;
      }
    } catch (err) {
      this.logWarn('currentTicketStatus-get', err);
      throw err;
    }
    return null;
  }

  get branchQuoteSupportId() {
    return this.currentTicketId;
  }
  // Parity: original ETO view exposed id getter delegating to quoteItemContainer.item.id with fallback to _id.
  get id(): string {
    console.log('Getting ID for FranchiseeQuoteItemView');
    return this.quoteItemContainer?.item.id ?? this._id;
  }
  get priceOrQtyChanged() {
    if (!this.quoteItemContainer) return false;
    if (this.isNew()) return true;
    return (
      this.originalsupplierGrossSingleUnitCost !== this.quoteItemContainer.price.supplierGrossSingleUnitCost ||
      this.originalQty !== this.quoteItemContainer.item.quantity
    );
  }

  get quantityFromUi() {
    try {
      return this.dataTracker.getEditorValue('quantity') as number;
    } catch (err) {
      this.logWarn('quantityFromUi', err);
      return 1;
    }
  }

  // ------------- QuoteFrameDataProvider (parent) -------------
  get supplierReferenceOverrideKey(): string {
    return this.quoteManager.quote.quoteOwnerId;
  }
  // QuoteFrameDataProvider requires supplierId; expose as field to avoid accessor override conflict.
  readonly supplierId: string = this.quoteManager?.quote?.supplierId ?? '';
  get quoteItem() {
    if (!this.quoteItemContainer) return undefined;
    return getV6QuoteItemForContainer(this.quoteItemContainer);
  }
  propertiesTemplate() {
    return html``;
  }
  get quoteIGUs(): unknown {
    const data = this.quoteManager.quoteProviderData() as V6FranchiseeQuoteProviderData;
    return data.igu ?? [];
  }
  get quoteDefaults(): unknown {
    const data = this.quoteManager.quoteProviderData() as V6FranchiseeQuoteProviderData;
    return data.defaultOptions ?? [];
  }
  get userMode(): string | undefined {
    return userSecurity().isPowerUser() ? v6FrameUserMode.powerUser : v6FrameUserMode.standardUser;
  }
  get quantity(): number {
    if (!this.quoteItemContainer) return 1;
    try {
      const uiValue = Math.max(1, this.quantityFromUi ?? 1);
      return uiValue;
    } catch {
      return this.quoteItemContainer?.item.quantity ?? 1;
    }
  }
  get supplierCostPrice(): number {
    return this.quoteItemContainer?.price.supplierGrossSingleUnitCost ?? 0;
  }
  customData() {
    return { isSpecialItem: true };
  }
  get thumbnailURL() {
    return '';
  }

  get qcm(): FranchiseeQuoteContainerManager {
    return this.quoteManager as FranchiseeQuoteContainerManager;
  }
  get forceReadonly() {
    return this.quoteManager.isReadonly() || this.supplierOffline;
  }
  get quoteSetId() {
    return this.quoteManager.quote.quoteSetId;
  }
  get quoteId() {
    return this.quoteManager.quoteId;
  }
  // Parity: original component surfaced hasPropertyDialog/hasModalEditDialog flags.
  public get hasPropertyDialog(): boolean {
    return true; // Always true as in original (dialog available for special items)
  }
  public get hasModalEditDialog(): boolean {
    return false; // No modal edit dialog in original merged path
  }
  get preSelectionFrameReference() {
    if (!this.supplierSelector) return undefined;
    return {
      reference: this.supplierSelector.objectReference,
      description: this.supplierSelector.note
    };
  }

  // ---------------- Parent overrides referencing child ----------------
  override isDataReadonly(): boolean {
    // Layer 1 & 2: Resource locks and quote state (genuine readonly)
    const genuineReadonly = this.isGenuineReadonly;

    // Layer 3: Support ticket state (blocks content editing but allows reactivation)
    const status = this.currentTicketStatus;
    const ticketTerminalReadonly =
      status !== null && flagEquals(status, BranchQuoteSupportStatus.Resolved, BranchQuoteSupportStatus.Cancelled);

    return genuineReadonly || ticketTerminalReadonly;
  }

  /**
   * Checks only genuine readonly conditions (resource locks + quote state).
   * Used for features like reactivation that should work even when ticket is in terminal state.
   */
  get isGenuineReadonly(): boolean {
    return super.isDataReadonly() || this.quoteManager.isReadonly();
  }
  get readonly(): boolean {
    return this.isDataReadonly() || super.readonly;
  }
  // Parity: original view included readyToClose guard to prevent closing while v6Editor busy.
  protected async readyToClose(): Promise<boolean> {
    const w = waitPatientlySaving();
    try {
      await this.v6BusyCheck();
    } finally {
      await w.hideModal();
    }
    return true;
  }

  // ---------------- Child logic (verbatim, renamed) ----------------
  private quantityCost(): number {
    try {
      const qty = (this.dataTracker.getEditorValue('quantity') as number) ?? 0;
      const unitCost = this.quoteItemContainer?.price.supplierGrossSingleUnitCost ?? 0;
      return money(qty * unitCost, 2);
    } catch {
      return this.quoteItemContainer?.price.quantityCost ?? 0;
    }
  }
  private eventCostOrQuantityChanged(updateCost: boolean) {
    if (!this.quoteItemContainer || this.usingV6) return;
    const qtyCost = this.quantityCost();
    const unitCost = this.dataTracker.getEditorValue('supplierGrossSingleUnitCost') as number;
    const qty = this.dataTracker.getEditorValue('quantity') as number;
    if (updateCost) {
      this.quoteItemContainer.price.singleUnitCost = 0;
      this.quoteItemContainer.price.supplierGrossSingleUnitCost = unitCost;
    }
    if (money(qty * unitCost, 2) !== qtyCost) this.requestUpdate();
  }
  private eventChangedSingleUnitCost = () => this.eventCostOrQuantityChanged(true);
  private eventChangedQuantity = () => this.eventCostOrQuantityChanged(false);

  private get supportService(): SupportTicketService | undefined {
    return this.qcm.supportTicketService;
  }
  private onRequestEditEntry = (e: CustomEvent<{ entry: ViewConversationEntry }>) => {
    e.stopPropagation();
    if (!this.supportService) return;
    const ticketId = this.currentTicketId;
    if (!ticketId) return;
    const session = this.supportService.getSession(ticketId);
    this.supportService.sessionSetEditingEntry(session, e.detail.entry.id);
  };
  private get editingEntry(): ViewConversationEntry | undefined {
    return this.entries.find(e => e.id === this.editingEntryId);
  }

  private _onCancelEditor = () => {
    try {
      const svc = this.supportService;
      if (!svc) return;
      const id = this.currentTicketId;
      if (!id) return;
      const session = svc.getSession(id);
      svc.sessionSetEditingEntry(session, null);
    } catch (err) {
      this.logWarn('_onCancelEditor', err);
    }
  };

  private prepareBindings() {
    const addDynamicMoney = (
      fieldName: string,
      getter: EventValueGetter,
      setter: EventValueSetter,
      readonly?: () => boolean
    ) => {
      this.dataTracker.addBinding(
        new DynamicValueBinder(FieldType.money, false, getter, setter, readonly),
        fieldName,
        fieldName,
        FieldType.money,
        false
      );
    };

    this.dataTracker.addDynamic(
      'title',
      FieldType.string,
      () => {
        // Prefer live item title; fallback to draft metadata subject if present; else empty
        try {
          const svc = this.qcm.supportTicketService;
          // Session-centric: prefer live item title; fallback to draft session metadata subject if draft id tracked
          let draftSubject: string | null = null;
          try {
            if (this._draftTicketId) {
              const session = svc.getSession(this._draftTicketId);
              draftSubject = session.draftMeta?.subject ?? null;
            }
          } catch {}
          return this.quoteItemContainer?.item?.title ?? draftSubject ?? '';
        } catch {
          return this.quoteItemContainer?.item?.title ?? '';
        }
      },
      (value: string) => {
        if (this.quoteItemContainer && this.quoteItemContainer.item) {
          this.quoteItemContainer.item.title = value;
        }
        try {
          const service: SupportTicketService = this.qcm.supportTicketService;
          const subject = value || '';
          const id = this.currentTicketId;
          if (id) {
            if (this.isNew() && this._draftTicketId) {
              service.updateDraftSession(this._draftTicketId, { subject });
            } else {
              service.beginMetadataEdit(id);
              service.updateDraftMetadata(id, { subject });
            }
          }
        } catch (err) {
          this.logWarn('binding-title-updateSubject', err);
        }
      },
      { nullable: false }
    );
    this.dataTracker.addDynamic(
      'comment',
      FieldType.string,
      () => this.quoteItemContainer?.item?.comment,
      () => {},
      { nullable: true }
    );
    this.dataTracker.addDynamic(
      'commentToManufacturer',
      FieldType.string,
      () => this.quoteItemContainer?.item?.commentToManufacturer,
      () => {},
      { nullable: true }
    );
    this.dataTracker.addDynamic(
      'description',
      FieldType.string,
      () => this.quoteItemContainer?.item?.description ?? '',
      (value: string) => {
        if (this.quoteItemContainer && this.quoteItemContainer.item) {
          this.quoteItemContainer.item.description = value;
        }
      },
      { nullable: false }
    );

    this.dataTracker.addObjectBinding(
      () => this.quoteItemContainer?.item,
      'quantity',
      'quantity',
      FieldType.float,
      false
    );
    addDynamicMoney(
      'supplierGrossSingleUnitCost',
      () => this.quoteItemContainer?.price.supplierGrossSingleUnitCost ?? 0,
      () => {},
      () => true
    );
  }

  connectedCallback(): void {
    super.connectedCallback();
    const svc = this.qcm.supportTicketService;
    this._subscribeSnapshots(svc);
    this._registerEtoTicketHooks();
    // Initialize draft session if new ETO item (forced ticket id = dataPrimaryKey)
    if (this.isNew()) {
      this._draftTicketId = svc.createSupportTicketDraft({
        quoteId: this.quoteId,
        supportType: BranchQuoteSupportItemType.EngineeredToOrder,
        branchQuoteSupportId: this.dataPrimaryKey,
        conversationEntryId: this.dataPrimaryKey,
        supplierName: this.qcm.supplierName,
        masterDocumentConversationId: this.masterDocumentConversationId
      });
      // Seed initial subject based on title
      svc.updateDraftSession(this._draftTicketId, { subject: this.quoteItemContainer?.item.title ?? '', text: '' });
    }
  }

  private _unsubscribe?: () => void; // align with ticket editor naming
  private _subscribeSnapshots(svc: SupportTicketService) {
    if (!svc) return;
    interface TicketDelta {
      reason?: string;
      changedTicketIds: string[];
    }
    this._unsubscribe = svc.subscribe((state: unknown, delta: TicketDelta) => {
      const snapshot = state as {
        activeTicketId?: string | null;
        tickets: Record<string, { ticket?: BranchQuoteSupport | null }>;
      };
      // Consolidated new ticket workflow handling
      if (this.isNew()) {
        this.v6Editor?.requestUpdate();
        this.requestUpdate();
        this.dispatchUiChanged();
        return;
      }
      if (this.currentTicketId === null) return;
      if (!delta.changedTicketIds.includes(this.currentTicketId)) return;
      const snap = snapshot.tickets[this.currentTicketId];
      if (snap?.ticket) {
        this.v6Editor?.requestUpdate();
        this.requestUpdate();
        this.dispatchUiChanged();
      }
    });
  }
  disconnectedCallback(): void {
    try {
      this.qcm.supportTicketService.disposeSession(this.currentTicketId!);
    } catch {
      /* ignore */
    }
    if (this._unsubscribe) {
      try {
        this._unsubscribe();
      } catch {
        /* ignore */
      }
      this._unsubscribe = undefined;
    }
    // Clean up lifecycle hooks to prevent firing on other screens
    this._unregisterEtoTicketHooks();
    super.disconnectedCallback();
  }
  async updated(changed: Map<string, unknown>): Promise<void> {
    // Previous implementation re-hydrated and staged session state here, but prepareEditor() now handles
    // ticket opening, conversation loading, and draft staging. This lifecycle hook is reduced to a noop
    // for branchQuoteSupport changes to avoid disposing active sessions and losing editing state.
    if (!changed.has('branchQuoteSupport')) return;
    if (this.updatingFromSnapshot) return; // guard against recursive updates
    // Intentionally no session dispose/open logic here.
  }

  // ---------------- Parent method copied verbatim (loadBranchQuoteSupport) ----------------
  public async loadBranchQuoteSupport(quoteItemId: string): Promise<boolean> {
    const supportData = await this.qcm.needsQuoteSupport(); //always reload a fresh copy
    this.quoteItemConversationLink = await this.franchiseeApi.quoteItemConversationLink({
      quoteId: this.quoteManager.quote.id,
      quoteItemId: quoteItemId,
      type: QuoteItemConversationType.EngineeredToOrder,
      conversationId: quoteItemId
    });
    if (!this.quoteItemConversationLink) return false; // Ensure conversation link is valid
    const branchQuoteSupport = supportData?.items.find(
      x => x.conversationId === this.quoteItemConversationLink?.conversationId
    );
    if (!branchQuoteSupport) return false;
    this.masterDocumentConversationId = branchQuoteSupport.masterDocumentConversationId ?? emptyGuid;
    this.branchQuoteSupport = branchQuoteSupport;
    await this.qcm.supportTicketService.openTicket(branchQuoteSupport.id, { edit: true });
    return branchQuoteSupport !== undefined;
  }

  // ---------------- Parent UI & behavior merged with child render ----------------
  buttonMenu(): Snippet {
    const isNew = this.isNew();
    if (isNew) {
      // New forced-ID draft path: mirror support-ticket-editor-view buttons
      return html`${this.abandonAndCloseButton()}
        <webmodule-button size="small" variant="primary" @click=${() => this.issueNewTicket()}>
          ${LangBranchQuoteSupportNew.issueTicket()}
        </webmodule-button>`;
    }
    return html`${this.abandonAndCloseButton()}<support-ticket-actions
        .ticketId=${this.currentTicketId ?? ''}
        .quoteManager=${this.qcm as any}
        .readonly=${this.readonly}
        .forceReadonly=${this.forceReadonly}
        hideWhenDisabled
        @support:intent:status-change=${(e: CustomEvent) => this._onStatusChangeIntent(e)}
      ></support-ticket-actions>`;
  }
  private async _onStatusChangeIntent(e: CustomEvent<{ ticketId?: string; nextStatus: BranchQuoteSupportStatus }>) {
    const detail = e.detail;
    const svc = this.qcm.supportTicketService;
    const ticketId = detail.ticketId; // activeTicketId removed; detail.ticketId required
    if (!ticketId) return;
    const state = svc.getState();
    const ticketSnap = state.tickets?.[ticketId];
    const ticket = ticketSnap?.ticket;
    let issues: string[] = [];
    await lockUIandExecute(async () => {
      let allowChange = false;

      //we need to rebind any v6 data before changing status, to ensure validations and content are up to date.
      if (this.usingV6) await this.commitV6Data();

      switch (e.detail.nextStatus) {
        case BranchQuoteSupportStatus.Cancelled:
          allowChange = await AskConfirmation(
            LangFranchiseeEngineeredToOrder.cancelEtoNotification(),
            buttonsContinueCancel(),
            undefined,
            LangFranchiseeEngineeredToOrder.continueSpecialItemBqsCancel()
          );
          break;
        case BranchQuoteSupportStatus.Resolved:
          issues = await this.getResolveIssues();
          if (issues.length > 0) {
            await information(
              LangFranchiseeEngineeredToOrder.resolveEtoNotification({ issues: stringsAsMarkdownList(issues) }),
              LangFranchiseeEngineeredToOrder.specialItemValidationIssues()
            );
            allowChange = false;
          } else allowChange = true;
          break;
        case BranchQuoteSupportStatus.New:
          await this.issueNewTicket();
          return;

        default:
          allowChange = true;
      }

      if (!ticket) return; // cannot update status without persisted ticket
      if (allowChange)
        await svc.updateStatus({
          branchQuoteSupportId: ticketId,
          previousStatus: ticket.status,
          status: detail.nextStatus
        });
    });
  }

  // Adapter getter to avoid paradoxical same-type assignment error by narrowing to public surface
  // adapter removed: using double unknown cast to satisfy identical type assignment without any

  private ensureNewDraftSubjectSync() {
    try {
      if (!this.quoteItemContainer || !this._draftTicketId) return;
      const svc = this.qcm.supportTicketService;
      const title = this.quoteItemContainer.item.title || '';
      svc.updateDraftSession(this._draftTicketId, { subject: title });
    } catch {}
  }

  private async issueNewTicket() {
    const svc = this.qcm.supportTicketService;
    // One-step issuance: draft creation OR persisted Draft promotion both route through saveTicket with status override.
    const candidateId = this._draftTicketId ?? this.currentTicketId;
    if (!candidateId) return;
    const session = svc.getSession(candidateId);
    if (!session) return;
    const isDraftCreation = session.kind === 'draft';
    const isDraftPromotion = session.kind === 'editing' && session.ticket?.status === BranchQuoteSupportStatus.Draft;
    if (!isDraftCreation && !isDraftPromotion) return;
    if (isDraftCreation) this.ensureNewDraftSubjectSync();
    const errors = isDraftCreation ? svc.validateDraftSession(candidateId) : [];
    if (errors.length) {
      await showValidations(errors);
      return;
    }
    await lockUIandExecute(async () => {
      await svc.saveTicket(candidateId, {
        finalizeDraftEntry: true,
        status: BranchQuoteSupportStatus.New,
        combinedPublish: true
      });
    });
  }

  protected getCaption(): Snippet {
    return getQuoteItemTab(
      firstValidString(this.quoteItemContainer?.item.title, LangFranchiseeEngineeredToOrder.specialItem()),
      this.quoteManager.itemPosition(this.quoteItemContainer?.item.id)
    );
  }
  discardLabel(): unknown {
    if (this.isDataReadonly()) return LangFranchiseeEngineeredToOrder.close();
    return this.isNew() ? LangFranchiseeEngineeredToOrder.discard() : LangFranchiseeEngineeredToOrder.discardChanges();
  }
  protected name() {
    return 'engineered-to-order';
  }

  protected bodyTemplate(): EventTemplate {
    return this.renderMerged();
  }

  private renderExternalEditor() {
    // Detach from legacy branchQuoteSupport object: rely on unified currentTicketId + snapshot-driven entries/editing state.
    const ticketId = this.currentTicketId;
    if (!ticketId) return '';
    // When creating a brand-new ticket draft, suppress the conversation entry editor and show passive draft UI like wm-branchquote-support-new.
    const svc = this.qcm.supportTicketService;
    if (this.isNew()) {
      return this.renderNewDraftConversation(svc);
    }
    const entry = this.editingEntry;
    const evalResult = this.evaluateCapabilities();
    // Show editor when editing an entry OR ticket open & user can add a note OR there are zero entries (initial topic creation)
    const allowCreate = !this.ticketClosed && (evalResult.actions.addNote || this.entries.length === 0);
    const show = !!entry || allowCreate;
    if (!show) return '';
    const mode = entry ? 'edit' : 'create';

    // Header logic:
    // 1. No entries OR single draft entry -> initial topic
    // 2. Editing a draft entry -> edit comments
    // 3. Otherwise -> add comments
    const initialTopic = this.entries.length === 0 || (this.entries.length === 1 && this.entries[0]?.draft);
    const header = initialTopic
      ? LangFranchiseeEngineeredToOrder.supportInitialTopic()
      : entry && entry.draft
        ? LangFranchiseeEngineeredToOrder.editComments()
        : LangFranchiseeEngineeredToOrder.addComments();

    return html`<div class="conversation-note">
      <h2 class="main-header-style">${header}</h2>
      <wm-support-conversation-entry-editor
        .ticketId=${ticketId}
        .mode=${mode}
        .entry=${entry}
        .quoteManager=${this.qcm}
        @cancel=${this._onCancelEditor}
      ></wm-support-conversation-entry-editor>
    </div>`;
  }

  // Passive new ticket draft UI (initial topic prior to first persisted conversation entry)
  private _newDraftEditorHost: HTMLElement = document.createElement('div');
  private _newDraftEditor?: import('@toast-ui/editor').Editor;
  private _onDraftAttachmentsChanged = () => {
    try {
      const svc = this.qcm.supportTicketService;
      if (!this._draftTicketId) return;
      const attachmentsEl = this.renderRoot?.querySelector('#newDraftAttachments') as
        | WebModuleConversationEntryDropZone
        | undefined;
      const files = attachmentsEl?.allFilesAsInputs ?? [];
      svc.updateDraftSession(this._draftTicketId, { attachments: files });
    } catch (err) {
      this.logWarn('draftAttachmentsChanged', err);
    }
  };
  private renderNewDraftConversation(svc: SupportTicketService) {
    const draftTicketId = this._draftTicketId ?? this.currentTicketId ?? '';
    const session = draftTicketId ? svc.getSession(draftTicketId) : undefined;
    const draftConversationEntryId = session?.draftEntry?.id ?? draftTicketId;
    const text = session?.draftEntry?.text ?? '';
    this.ensureNewDraftEditor(svc, text);
    return html`<div class=" conversation-note conversation-new-draft">
      <h2 class="main-header-style">${LangFranchiseeEngineeredToOrder.supportInitialTopic()}</h2>
      <div class="conversation-initial-text mb-3">${this._newDraftEditorHost}</div>
      <div class="conversation-initial-attachments mb-2">
        <wmview-conversation-attachments
          id="newDraftAttachments"
          .conversationId=${draftTicketId}
          .conversationEntryId=${draftConversationEntryId}
          @wm-event-changed=${this._onDraftAttachmentsChanged}
        ></wmview-conversation-attachments>
      </div>
    </div>`;
  }
  private ensureNewDraftEditor(svc: SupportTicketService, currentText: string) {
    try {
      if (!this._draftTicketId) return;
      // Instantiate once when host is connected
      if (!this._newDraftEditor && this._newDraftEditorHost.isConnected) {
        const editorFactory = (window as any).newStandardEditor || undefined;
        // Use imported factory if available
        if (typeof editorFactory !== 'function') {
          // Fallback to direct import (tree-shaken) - dynamic require not needed since we import at top in original file.
        }
        // Importing function directly (declared elsewhere) - assume global newStandardEditor imported; else cast.
        this._newDraftEditor = newStandardEditor(this._newDraftEditorHost, currentText, 200);
        (this._newDraftEditor as any).on('change', () => {
          try {
            const text = (this._newDraftEditor as any).getMarkdown?.() ?? '';
            if (this._draftTicketId) svc.updateDraftSession(this._draftTicketId, { text });
          } catch (err) {
            this.logWarn('draftEditorChange', err);
          }
        });
      } else if (this._newDraftEditor) {
        // Keep editor content in sync if draft text updated elsewhere
        const current = (this._newDraftEditor as any).getMarkdown?.() ?? '';
        if (current !== currentText) {
          (this._newDraftEditor as any).setMarkdown?.(currentText);
        }
      }
    } catch (err) {
      this.logWarn('ensureNewDraftEditor', err);
    }
  }

  private createCommentButton(fieldName: string, forms: FormInputAssistant) {
    const currentValue = this.dataTracker.getObjectValue(fieldName) as string;
    if (forms.forceReadonly) {
      return {
        label: LangFranchiseeEngineeredToOrder.viewNote(),
        readonly: !currentValue || isEmptyOrSpace(currentValue)
      };
    }
    return {
      label: isEmptyOrSpace(currentValue)
        ? LangFranchiseeEngineeredToOrder.addNote()
        : LangFranchiseeEngineeredToOrder.editNote(),
      readonly: false
    };
  }
  private async handleCommentEdit(
    fieldName: 'comment' | 'commentToManufacturer',
    title: string,
    forms: FormInputAssistant
  ) {
    await handleCommentEdit(fieldName, title, this.quoteItemContainer, forms.forceReadonly, () => this.requestUpdate());
  }

  /**
   * Dropdown that assigns this item to one of the quote's item groups (a dealer-side concept, not a
   * V6 property). Selecting a group moves the item into it; clearing it removes it from its group.
   * Only shown when the item is groupable and already part of the grouping document (a brand-new,
   * unsaved item is not yet known to the document). Mirrors the V6 frame editor's group selector.
   *
   * `readonly` is the editor's computed readonly (from `evaluateCapabilities`), so the field is also
   * disabled when the whole editor is locked - e.g. a resolved support ticket - not just when the
   * quote itself is readonly.
   */
  private groupSelectorTemplate(forms: FormInputAssistantBase, readonly: boolean): TemplateResult {
    const item = this.quoteItemContainer?.item;
    if (!item || this.quoteManager.itemIsNotSortable(item) || isDeletedQuoteItemState(item)) return html``;

    const doc = this.quoteManager.quoteItemGrouping;
    if (!doc) return html``;
    // the item must be part of the grouping document (saved + groupable). Groups may be empty - the
    // combobox's allow-create lets the user create the first group from here.
    const groups = getGroups(doc);
    if (!allItemIds(doc).has(item.commonId)) return html``;

    // the value the combobox should show: a pending new-group name, then a pending existing/trunk
    // choice, otherwise the persisted group
    const selectedValue =
      this._pendingNewGroupName ??
      (this._pendingGroupId !== undefined ? (this._pendingGroupId ?? '') : (getItemGroupId(doc, item.commonId) ?? ''));

    // no explicit "ungrouped" option: an empty value shows the placeholder, and the combobox's
    // clear button moves the item back to the trunk
    const values: FormInputSelectValue[] = groups.map(group => ({
      value: group.id,
      text: group.name,
      disabled: false
    }));

    return forms.combobox({
      label: LangV6QuoteItemFrameView.groupAssignment(),
      value: selectedValue,
      values,
      allowCreate: true,
      clearable: true,
      placeholder: LangV6QuoteItemFrameView.groupPlaceholder(),
      emptyText: LangV6QuoteItemFrameView.noGroupsYet(),
      getCreateLabel: (name: string) => LangV6QuoteItemFrameView.createGroupOption({ name }),
      readonly: readonly || this.quoteManager.isReadonly(),
      events: {
        change: (e: Event) => {
          const value = ((e.target as HTMLElement & { value?: string }).value ?? '').toString();
          this.selectPendingGroup(value);
        },
        create: (e: CustomEvent<{ value: string }>) => this.selectPendingNewGroup(e.detail.value)
      }
    });
  }

  /** the item's persisted group id (null when in the trunk / not grouped). */
  private persistedGroupId(): string | null {
    const item = this.quoteItemContainer?.item;
    const doc = this.quoteManager.quoteItemGrouping;
    if (!item || !doc) return null;
    return getItemGroupId(doc, item.commonId);
  }

  /** true when a deferred group assignment (existing, trunk, or new) differs from the persisted group. */
  private hasPendingGroupChange(): boolean {
    if (this._pendingNewGroupName !== null) return true;
    return this._pendingGroupId !== undefined && this._pendingGroupId !== this.persistedGroupId();
  }

  /** record the chosen existing group (empty value => trunk) without persisting; applied on save. */
  private selectPendingGroup(rawValue: string): void {
    // ignore the change that fires right after creating a new group (value === the new name)
    if (this._pendingNewGroupName !== null && rawValue === this._pendingNewGroupName) return;
    this._pendingNewGroupName = null;
    this._pendingGroupId = isEmptyOrSpace(rawValue) ? null : rawValue;
    // re-render so the dirty/save state reflects the pending change
    this.refreshParent();
  }

  /** record a brand-new group name to create on save. */
  private selectPendingNewGroup(name: string): void {
    this._pendingNewGroupName = isEmptyOrSpace(name) ? null : name.trim();
    this._pendingGroupId = undefined;
    this.refreshParent();
  }

  /**
   * Apply the deferred group assignment. Called from the save flow AFTER the item itself has been
   * saved, so the move runs against the freshly persisted item (correct record version) instead of
   * mid-edit. The move persists itself (grouping save + any exclude-from-pricing reprice).
   */
  private async applyPendingGroupAssignment(): Promise<void> {
    const item = this.quoteItemContainer?.item;
    const doc = this.quoteManager.quoteItemGrouping;
    if (!item || !doc) return;

    // resolve the target group: create a new one first if the user typed a new name
    let groupId: string | null;
    if (this._pendingNewGroupName !== null) {
      const created = await this.quoteManager.createItemGroup(this._pendingNewGroupName);
      if (!created) return;
      // the group is now persisted; demote the pending state to an existing-group move so that if
      // the move below fails, a retry moves into the created group instead of creating a duplicate
      this._pendingNewGroupName = null;
      this._pendingGroupId = created;
      groupId = created;
    } else if (this._pendingGroupId !== undefined) {
      groupId = this._pendingGroupId;
    } else {
      return;
    }

    // append to the end of the target group (or the trunk when ungrouping). Re-read the doc since
    // createItemGroup may have rebuilt it.
    const currentDoc = this.quoteManager.quoteItemGrouping ?? doc;
    const targetEnd =
      groupId === null ? currentDoc.root.length : (findGroup(currentDoc, groupId)?.children.length ?? 0);
    const moved = await this.quoteManager.moveItemToGroup(item.commonId, groupId, targetEnd);
    if (moved) {
      this._pendingGroupId = undefined;
      this._pendingNewGroupName = null;
      // the move may have bumped the item's record version; re-sync the held container
      this.quoteItemContainer = this.quoteManager.quoteItemContainer(item.id);
    }
  }

  private renderMerged() {
    const evalResult = this.evaluateCapabilities();
    const readonly = evalResult.readonly;
    const forms = new FormInputAssistant(this.dataTracker, readonly, true);
    const formsNonForced = new FormInputAssistantBase(false);
    const customerNoteButton = this.createCommentButton('comment', forms);
    const manufacturerNoteButton = this.createCommentButton('commentToManufacturer', forms);
    const handleCustomerNote = () =>
      this.handleCommentEdit('comment', LangFranchiseeEngineeredToOrder.notesToClient(), forms);
    const handleManufacturerNote = () =>
      this.handleCommentEdit('commentToManufacturer', LangFranchiseeEngineeredToOrder.notesToManufacturer(), forms);

    // Build button for client notes
    const clientNoteButtonElement = formsNonForced.buttonPrimary(customerNoteButton.label, {
      events: { click: handleCustomerNote },
      readonly: customerNoteButton.readonly
    });
    // Conditionally wrap in preview popup
    const clientNoteSlotted = this.quoteItemContainer?.item.comment
      ? createContentPreview(
          clientNoteButtonElement,
          LangFranchiseeEngineeredToOrder.notesToClient(),
          this.quoteItemContainer.item.comment
        )
      : clientNoteButtonElement;

    // Build button for manufacturer notes
    const manufacturerNoteButtonElement = formsNonForced.buttonPrimary(manufacturerNoteButton.label, {
      events: { click: handleManufacturerNote },
      readonly: manufacturerNoteButton.readonly
    });
    // Conditionally wrap in preview popup
    const manufacturerNoteSlotted = this.quoteItemContainer?.item.commentToManufacturer
      ? createContentPreview(
          manufacturerNoteButtonElement,
          LangFranchiseeEngineeredToOrder.notesToManufacturer(),
          this.quoteItemContainer.item.commentToManufacturer
        )
      : manufacturerNoteButtonElement;
    const conversation = html`<div class="border-top border-secondary mt-2 ">
      <wm-conversation-view
        .readonly=${this.qcm.isReadonly()}
        emptyNote=""
        .supplierName=${this.qcm.supplierName ?? ''}
        ?sortAscending=${true}
        .entries=${this.entries}
        @request-edit-entry=${this.onRequestEditEntry}
      ></wm-conversation-view>
    </div>`;

    const src = isEmptyOrSpace(this.quoteItemContainer?.item.virtualThumbnailPath)
      ? ''
      : this.quoteApi.convertQuoteItemAssetPathToUrl(
          this.quoteId,
          this.quoteItemContainer?.item.id,
          this.quoteItemContainer?.item.virtualThumbnailPath,
          // Without a tag this URL never changes, so a newly uploaded image is never re-requested
          // and the editor keeps showing the old one. See assetContentTag.
          assetContentTag(
            this.quoteItemContainer?.data?.assetVirtualPaths,
            'thumbnail',
            this.quoteItemContainer?.item.recordVersion
          )
        );
    const isSupplier = this.claims.isAgent;
    const dealerAccess = !isSupplier || (isSupplier && supplierHasFullPrivelages());

    const eventMasterDocument = async (
      e: CustomEvent<{
        masterDocumentFilename: string | null;
      }>
    ) => {
      const masterDocumentAttachment = e.detail.masterDocumentFilename;
      const svc = this.qcm.supportTicketService;
      if (this.isNew()) {
        if (this._draftTicketId) {
          svc.updateDraftSession(this._draftTicketId, { masterDocumentFilename: masterDocumentAttachment ?? null });
        }
        return;
      }
      const id = this.currentTicketId;
      if (!id) return;

      svc.updateDraftMetadata(id, {
        masterDocumentFilename: masterDocumentAttachment ?? null
      });
    };
    const convTemplate = html`<div class="conversation-header-block">
        <h2 class="main-header-style border-0">${LangFranchiseeEngineeredToOrder.comments()}</h2>
      </div>
      <div class="eto-edit-conversation">${conversation}</div>`;
    const tabGroup = this.usingV6
      ? html`<webmodule-tab-group size="small"
          ><webmodule-tab slot="nav" panel="optionsTab" active
            >${LangFranchiseeEngineeredToOrder.optionsTab()}</webmodule-tab
          ><webmodule-tab slot="nav" panel="conversationTab"
            >${LangFranchiseeEngineeredToOrder.conversationTab()}</webmodule-tab
          ><webmodule-tab-panel name="optionsTab">${this.v6Editor?.ui}</webmodule-tab-panel
          ><webmodule-tab-panel name="conversationTab">${convTemplate}</webmodule-tab-panel></webmodule-tab-group
        >`
      : html``;

    // Resolve master document conversation id once for template bindings
    const svc = this.qcm.supportTicketService;

    const masterDocumentFilename = this.isNew()
      ? this._draftTicketId
        ? svc.getSession(this._draftTicketId).draftMeta?.masterDocumentFilename
        : null
      : svc.getSession(this.currentTicketId!).draftMeta?.masterDocumentFilename;

    const masterDocumentPublicUrl = this.getMasterDocumentPublicUrl();
    const masterDocumentDropZoneTemplate = html`<div class="eto-master-document">
      <wm-eto-masterdocument-uploader
        id="masterdocument"
        .caption=${LangFranchiseeEngineeredToOrder.shopDrawing()}
        .masterDocumentConversationId=${this.masterDocumentConversationId}
        .masterDocumentFilename=${masterDocumentFilename ?? ''}
        .masterDocumentPublicUrl=${masterDocumentPublicUrl ?? ''}
        .readonlyCaption=${isSupplier ? '' : LangFranchiseeEngineeredToOrder.supplierToUpload()}
        .disabled=${readonly || !isSupplier || !evalResult.actions.addNote}
        @wm-eto-masterdocument-uploaded=${eventMasterDocument}
      ></wm-eto-masterdocument-uploader>
    </div>`;
    const leftColumnTemplate = html`<div
        class="main-header d-flex justify-content-between border-bottom border-secondary mb-2"
      >
        <h2 class="main-header-style border-0">
          ${LangFranchiseeEngineeredToOrder.specialItem()}
          ${getSupportStatusBadge(this.currentTicketStatus ?? BranchQuoteSupportStatus.Draft)}
        </h2>
      </div>
      <form class="special-item-editor-container ">
        <div class="row">
          <div class="col-6 custom-item-props">
            ${dealerAccess
              ? forms.textRequired('title', LangFranchiseeEngineeredToOrder.title(), 120, {
                  change: () => {
                    const newVal = forms.dataBinding.getValue('title');
                    const svc = this.qcm?.supportTicketService;
                    if (!svc) return;
                    try {
                      if (this.isNew() && this._draftTicketId) {
                        svc.updateDraftSession(this._draftTicketId, { subject: newVal });
                      } else {
                        const id = this.currentTicketId;
                        if (id) {
                          svc.beginMetadataEdit(id);
                          svc.updateDraftMetadata(id, { subject: newVal });
                        }
                      }
                    } catch {
                      /* logging pending */
                    }
                  }
                })
              : forms.textReadonly('title', LangFranchiseeEngineeredToOrder.title())}${dealerAccess
              ? forms.float('quantity', LangFranchiseeEngineeredToOrder.quantity(), {
                  events: { blur: this.eventChangedQuantity, keyup: this.eventChangedQuantity }
                })
              : forms.floatReadonly('quantity', LangFranchiseeEngineeredToOrder.quantity())}${this.usingV6
              ? forms.text('description', LangFranchiseeEngineeredToOrder.description(), 120)
              : isSupplier
                ? forms.textRequired('description', LangFranchiseeEngineeredToOrder.supplierReference(), 120)
                : forms.textReadonly('description', LangFranchiseeEngineeredToOrder.supplierReference())}${this.usingV6
              ? nothing
              : isSupplier
                ? forms.money('supplierGrossSingleUnitCost', LangFranchiseeEngineeredToOrder.supplierUnitPrice(), {
                    events: {
                      blur: this.eventChangedSingleUnitCost,
                      keyup: this.eventChangedSingleUnitCost
                    }
                  })
                : forms.moneyReadonly(
                    'supplierGrossSingleUnitCost',
                    LangFranchiseeEngineeredToOrder.supplierUnitPrice()
                  )}${this.groupSelectorTemplate(formsNonForced, readonly)}
          </div>
          <div class="col-3 upload-zone">
            <bs-form-image-upload
              id="thumbnail"
              .resize=${true}
              .showFileInput=${false}
              .isThumbnail=${true}
              .displayType=${1}
              .resizeWidth=${600}
              .resizeHeight=${500}
              .value=${src}
              .clickCaption=${isSupplier
                ? LangFranchiseeEngineeredToOrder.chooseFile()
                : LangFranchiseeEngineeredToOrder.supplierToUpload()}
              data-label=${LangFranchiseeEngineeredToOrder.thumbnail()}
              .readonly=${!isSupplier || readonly}
            ></bs-form-image-upload>
          </div>
          <div class="col-3 upload-zone last">${masterDocumentDropZoneTemplate}</div>
          <div class="col-12 custom-item-notes">
            ${formsNonForced.multiEditRow({
              title: LangFranchiseeEngineeredToOrder.notesToClient(),
              toolTip: LangFranchiseeEngineeredToOrder.thisDetailIsPublishedOnQuoteReports(),
              slotted: clientNoteSlotted
            })}
            ${formsNonForced.multiEditRow({
              title: LangFranchiseeEngineeredToOrder.notesToManufacturer(),
              toolTip: LangFranchiseeEngineeredToOrder.notesToManufacturerTooltip(),
              slotted: manufacturerNoteSlotted
            })}
          </div>
        </div>
      </form>
      <div class="conversation-note ${this.isNew() ? 'new-support-ticket' : ''}">${this.renderExternalEditor()}</div>`;
    const rightColumnTemplate = this.usingV6 ? tabGroup : convTemplate;
    return html`<div class="eto-main container form-two-col ">
      ${this.debugReadonlyBanner(evalResult)}
      <div class="row">
        <div class="col-5 form-column">${leftColumnTemplate}</div>
        <div class="col-7 form-column">${rightColumnTemplate}</div>
      </div>
    </div>`;
  }

  // ---------------- Parent workflow -> child methods ----------------
  async prepareEditor(): Promise<void> {
    if (this.quoteItemContainer && isSSI(this.quoteItemContainer.item)) {
      this._readyToEdit = false;
      return;
    }
    this._isNewItem = false;
    // preload the grouping document so the properties panel can offer group assignment. Grouping is
    // optional (not every quote/context has groups), so a load failure must not block the editor.
    try {
      await this.quoteManager.needsItemGrouping();
    } catch {
      // ignore - the group selector simply will not render
    }
    if (!this.quoteItemContainer) {
      let providerData: string | null = null;
      if (this.usingV6 && this.v6Editor) {
        let details: V6QuoteFrameEditorData | null = null;
        try {
          details = await this.v6Editor.commit();
          providerData = details ? (toJsonStr(details.quoteItem as object) ?? null) : null;
        } catch {
          await offline();
          return;
        }
      }
      this.createMockQuoteItem(providerData);
      this._isNewItem = true;
    }
    if (!this.quoteItemContainer) return;
    if (!isSpecialItem(this.quoteItemContainer.item))
      throw new DevelopmentError(
        `special-line-item-view, ${this.quoteItemContainer.item.title} is not a special line item.`
      );

    if (this.quoteItemContainer) {
      this.dataPrimaryKey = this.quoteItemContainer.item.id;
      if (this._readyToEdit && this.quoteItemContainer && this.usingV6) {
        await addV6DebugInformation('QuoteItemFrameViewForV6', {
          quoteItemId: this.quoteItemContainer.item.id,
          quoteItemPosition: this.quoteManager.itemPosition(this.quoteItemContainer.item.id)?.toString() ?? '',
          quoteTitle: this.quoteItemContainer.item.title,
          quoteItemDescription: this.quoteItemContainer.item.description
        });
      }
      if (!this._isNewItem)
        if (!(await this.loadBranchQuoteSupport(this.dataPrimaryKey))) {
          console.warn(`Failed to load BranchQuoteSupport for QuoteItem ${this.dataPrimaryKey}`);
        }
    }

    if (this.usingV6) {
      if (await v6BootLoaderWithFailMsg()) {
        if (!this.readonly) {
          const suppliers = await getQuoteSuppliers();
          this.supplierOffline = !(
            suppliers.find(x => x.supplierId === this.quoteManager.quote.supplierId)?.online ?? false
          );
          if (this.supplierOffline) await information(LangV6QuoteItemFrameView.supplierOfflineReadonly());
        }
        await addV6DebugInformation('WebModuleFranchiseeETOEditor', {
          quoteItemId: this.id,
          quoteItemPosition: this.quoteManager.itemPosition(this.id, true)?.toString() ?? '',
          quoteItemTitle: 'New Item'
        });
        this.v6Editor = await v6Editors().getQuoteFrameOptionsService(this);
        if (this.v6Editor) {
          this.v6Editor.ui.addEventListener('v6frameview', e => {
            const ev: CustomEvent = e as CustomEvent;
            if (ev.detail.error && ev.detail.shutdownRequired) this.abandonAndClose(true);
          });
        }
      }
      this._readyToEdit = this.v6Editor !== null && this.v6Editor !== undefined;
    } else this._readyToEdit = true;
    this.requestUpdate();
  }

  async prepareForSave(): Promise<void> {
    if (this.forceReadonly) return;
    await this.v6BusyCheck();
    this.dataTracker.applyChangeToValue();
  }
  public internalDataChanged(): boolean {
    // a deferred group assignment alone makes the editor dirty, even when the support ticket has no
    // pending changes of its own
    if (this.hasPendingGroupChange()) return true;
    const svc = this.qcm.supportTicketService;
    const ticketId = this.isNew() ? this._draftTicketId : this.currentTicketId;
    if (!ticketId) return false;
    return svc.needsSave(ticketId);
  }
  protected async internalSaveData(): Promise<boolean> {
    return await this.save();
  }

  // ---------------- Child methods adapted ----------------
  public getValidationErrors(): string[] {
    const errors: string[] = [];
    this.dataTracker.applyChangeToValue();
    if (!this.quoteItemContainer) throw new DevelopmentError('ETO QuoteItemContainer must exist');
    if (isEmptyOrSpace(this.quoteItemContainer.item.title))
      errors.push(LangFranchiseeEngineeredToOrder.pleaseAddTitleToDescribeSpecialItem());
    const quantity = this.quoteItemContainer?.item.quantity ?? 0;
    if (quantity <= 0) errors.push(LangFranchiseeEngineeredToOrder.pleaseProvideQuantityGreaterThanZero());
    if (quantity % 1 !== 0) errors.push(LangFranchiseeEngineeredToOrder.specialItemQuantityMustBeWholeNumber());

    if (this.isNew()) {
      // If a new ticket draft exists, attempt to create it as a draft (non-final) to persist progress.
      if (this._draftTicketId) {
        const svc = this.qcm.supportTicketService;
        const errors1 = svc.validateDraftSession(this._draftTicketId);
        if (errors1.length) {
          errors.push(...errors1);
        }
      }
    }

    return errors;
  }
  private getResolveIssues(): Promise<string[]> {
    const result = this.getValidationErrors();
    if (!this.quoteItemContainer) throw new DevelopmentError('ETO must have item');
    if (!this.usingV6 && isEmptyOrSpace(this.quoteItemContainer.item.description))
      result.push(LangFranchiseeEngineeredToOrder.specialItemReferenceMustBeSet());
    if (this.usingV6) {
      const data = getV6FrameForContainer(this.quoteItemContainer);
      const stuff = data?.validations?.filter(x => x.type === FrameValidationType.critical) ?? [];
      stuff.forEach(issue => {
        result.push(`${issue.title}: ${issue.text ?? ''}`);
      });
    }
    if (this.quoteItemContainer.item.quantity < 1) result.push(LangFranchiseeEngineeredToOrder.quantityMustBeValid());
    if (this.quoteItemContainer.item.quantity % 1 !== 0)
      result.push(LangFranchiseeEngineeredToOrder.quantityMustBeWholeNumber());
    if (isEmptyOrSpace(this.thumbNailEditor?.value))
      result.push(LangFranchiseeEngineeredToOrder.thumbnailMustBeUploaded());
    const masterDocAttachmentCount = (this.masterDocumentEditor?.allAttachmentsAsInputs || []).length;
    if (masterDocAttachmentCount < 1) result.push(LangFranchiseeEngineeredToOrder.shopDrawingFileMustBeIncluded());
    return Promise.resolve(result);
  }
  private async v6BusyCheck() {
    if (this.usingV6 && this.v6Editor) {
      const awaiter: Promise<void> | null | undefined = this.v6Editor.updateComplete;

      //undefined means not in the FC build. null means doesn't have anything to do.
      if (awaiter !== undefined) await awaiter;
      else {
        await sleep(20);
        while (this.v6Editor?.busy) {
          await sleep(20);
        }
      }
    }
  }
  private backupPriceDetails() {
    this.originalsupplierGrossSingleUnitCost = this.quoteItemContainer?.price.supplierGrossSingleUnitCost;
    this.originalQty = this.quoteItemContainer?.item.quantity;
    this.originalThumbnail = this.quoteItemContainer?.item.virtualThumbnailPath;
  }
  protected onQuoteItemContainerChanged() {
    super.onQuoteItemContainerChanged();
    this.backupPriceDetails();
    if (this.quoteItemContainer) {
      const quoteItem = getV6QuoteItemForContainer(this.quoteItemContainer);
      const originalFrameData = quoteItem ? v6Util().getFrameData(quoteItem) : null;
      this.originalFrameData = originalFrameData;
    }
  }
  private async setInfo(s: string, header = LangFranchiseeEngineeredToOrder.preparingToSave()) {
    await this._informationDispatcher?.setInformation(`# ${header}\n\n                + ${s}`);
  }

  private async save(): Promise<boolean> {
    if (!this.quoteItemContainer) return false;
    if (!this.internalDataChanged()) return true;

    const errors = this.getValidationErrors();
    if (errors.length) {
      await showValidations(errors);
      return false;
    }

    const informationDispatcher = new InformationDispatcher();
    this._informationDispatcher = informationDispatcher;
    await this.setInfo(LangFranchiseeEngineeredToOrder.gettingUpdatesFromSupplier());
    const saveModal = new SaveWorkflowModal(
      LangFranchiseeEngineeredToOrder.savingItemTitle({ title: this.quoteItemContainer.item.title }),
      informationDispatcher,
      3
    );

    saveModal.delayedShow();
    try {
      const svc = this.qcm.supportTicketService;
      if (this.isNew()) {
        // a brand-new item is not yet in the grouping document, so no group assignment can be
        // pending here; just persist the draft ticket if one is tracked
        if (this._draftTicketId) {
          const ok = await svc.saveTicket(this._draftTicketId, { finalizeDraftEntry: false });
          return ok;
        }
        return true; // no draft state tracked yet
      }
      // existing item: persist the ticket (a no-op when only the group changed), then apply any
      // deferred group assignment once the item is safely at its current persisted record version
      let ok = true;
      if (this.currentTicketId) ok = await svc.saveTicket(this.currentTicketId, { finalizeDraftEntry: false });
      if (ok && this.hasPendingGroupChange()) await this.applyPendingGroupAssignment();
      return ok;
    } finally {
      saveModal?.done();
      await saveModal?.hideModal();
      this._informationDispatcher = null;
    }
    return true;
  }
  private buyInDataJsonStr(buyInCosts?: readonly unknown[]): string | undefined {
    if (buyInCosts && buyInCosts.length > 0) {
      return toJsonStr(buyInCosts) ?? undefined;
    } else return undefined;
  }
  /**
   * Puts the freshly picked thumbnail into the pending asset payload, or removes the key when the
   * user has not picked one on this save. Never emits a delete - see buildThumbnailAsset for why.
   */
  private async applyThumbnailAsset() {
    // Always base64, never 'text'. The only content source here is base64Value(), which returns a
    // full data URL ("data:image/png;base64,...") whatever the user picked. The 'base64' encoding
    // strips that prefix and sends the bytes via AttachedBlobAsset.DataBase64, which the server
    // deserialises to a byte[] and stores as the real file. The 'text' encoding instead puts the
    // string into AttachedBlobAsset.Data, which the server UTF8-encodes verbatim - so the blob
    // becomes the literal text "data:image/png;base64,..." and every <img> pointing at it is broken.
    // The create path used to pass 'text' and did exactly that; the update path always passed
    // 'base64'. Per AttachedBlobAsset's contract, Data is for open text (JSON/CSS/SVG markup) only,
    // and nothing here ever supplies markup - so no caller gets to choose.
    const raw = this.thumbNailEditor?.isValueChanged ? ((await this.thumbNailEditor?.base64Value()) ?? '') : '';
    const asset = buildThumbnailAsset({
      hasNewFile: this.thumbNailEditor?.isValueChanged ?? false,
      content: base64DataStringToRawBase64(raw),
      assetPath: this.quoteApi.createQuoteItemAssetPath('thumbnail', this.thumbNailEditor?.fileExtension || '.svg'),
      encoding: 'base64'
    });

    if (asset) this.assets['thumbnail'] = asset;
    else delete this.assets['thumbnail'];
  }

  private async createNewItem(ssiInputs: InputUpdateQuoteItems | undefined): Promise<boolean> {
    if (!this.quoteItemContainer) return false;
    await this.applyThumbnailAsset();

    const result = await this.qcm.createQuoteItemEx(this.quoteItemContainer, this.assets, ssiInputs);
    if (!this.qcm.lastSaveSuccessful) return false;
    if (result) {
      await this.thumbNailEditor?.ClearFiles();

      this.quoteItemContainer = result;

      return true;
    }
    return false;
  }

  public async revertChanges(): Promise<boolean> {
    // drop any deferred group assignment
    this._pendingGroupId = undefined;
    this._pendingNewGroupName = null;
    if (!this.quoteItemContainer) return true;
    if (!this.isNew())
      this.quoteItemContainer =
        this.qcm.restoreQuoteItemFromBackup(this.quoteItemContainer.item.id) ?? this.quoteItemContainer;
    await this.thumbNailEditor?.ClearFiles();
    this.dataTracker.resetEditorValue();
    return true;
  }

  private async saveExistingItem(ssiInputs: InputUpdateQuoteItems | undefined): Promise<boolean> {
    if (!this.quoteItemContainer) return false;
    await this.applyThumbnailAsset();

    const result = await this.qcm.saveAndUpdateQuoteItem(this.quoteItemContainer, this.assets, ssiInputs, false);
    if (!this.qcm.lastSaveSuccessful) return false;
    await this.thumbNailEditor?.ClearFiles();
    this.quoteItemContainer = result;

    return true;
  }
  private async deleteExistingItem(ssiInputs: InputUpdateQuoteItems | undefined): Promise<boolean> {
    if (!this.quoteItemContainer) return false;
    await this.qcm.deleteQuoteItem(this.quoteItemContainer, ssiInputs);
    if (!this.qcm.lastSaveSuccessful) return false;
    this.quoteItemContainer = this.qcm.quoteItemContainer(this.quoteItemContainer.item.id);

    return true;
  }

  private async undeleteExistingItem(ssiInputs: InputUpdateQuoteItems | undefined): Promise<boolean> {
    if (!this.quoteItemContainer) return false;
    const result = await this.qcm.undeleteQuoteItem(this.quoteItemContainer, ssiInputs);
    if (!this.qcm.lastSaveSuccessful) return false;
    this.quoteItemContainer = result;
    return true;
  }

  private evaluateCapabilities() {
    return deriveSupportCapabilities({
      quoteManager: this.qcm,
      ticketStatus: this.currentTicketStatus ?? null,
      forceReadonly: this.forceReadonly,
      transientBusy: false,
      phase: 'eto'
    });
  }

  private getMasterDocumentPublicUrl(): string | null {
    try {
      const svc = this.qcm.supportTicketService;
      const state = svc.getState();
      const quoteId = this.quoteManager.quoteId;

      // Get the master document from the cached ticket list
      const list = (state as any).ticketLists?.[quoteId];
      if (!list) return null;

      const masterDocument = list.masterDocuments.find((md: any) => md.id === this.masterDocumentConversationId);
      if (!masterDocument) return null;

      // Return the public URL from the first attachment
      return masterDocument.attachments?.[0]?.publicUrl ?? null;
    } catch (err) {
      this.logWarn('getMasterDocumentPublicUrl', err);
      return null;
    }
  }
  private debugReadonlyBanner(evalResult: ReturnType<typeof this.evaluateCapabilities>) {
    return renderSupportDebugBanner(evalResult, 'ETO');
  }

  // ---------------- Existing parent helpers (unchanged) ----------------
  public isTab(): boolean {
    return (this.quoteItemContainer && !isSSI(this.quoteItemContainer.item)) ?? false;
  }
  get usingV6(): boolean {
    return this.supplierSelector !== undefined || isSupplierSpecialItem(this.quoteItemContainer?.item);
  }

  async dispose(): Promise<void> {
    if (!this.readonly) this.v6Editor?.releaseResources();
    this.v6Editor = null;
    await clearAllV6DebugInformation('WebModuleFranchiseeETOEditor');
    await super.dispose();
  }

  // Parent mock creation methods remain identical
  createMockQuoteItem(providerData?: string | null): void {
    const transactionDate = nowServer();
    const userId = getCurrentUser()?.id ?? emptyGuid;
    if (providerData)
      this.assets['data'] = {
        assetPath: this.quoteApi.createQuoteItemAssetPath('data', '.json'),
        data: providerData ?? null,
        delete: false,
        dataBase64: null,
        hasContent: false
      };
    this.quoteItemContainer = {
      item: {
        comment: '',
        commonId: newGuid(),
        dateCreated: transactionDate,
        description: '',
        id: this.dataPrimaryKey,
        isRestrictedToPowerUser: false,
        itemType: this.usingV6 ? QuoteItemType.Provider : QuoteItemType.Basic,
        lastModifiedDate: transactionDate,
        lastModifiedUserId: userId,
        providerReferenceId: emptyGuid,
        quantity: 1,
        quoteId: this.quoteManager.quoteId,
        quoteItemContentType: quoteItemContentType.specialItem,
        recordVersion: '',
        serviceProvider: this.usingV6 ? quoteSupplierProvider.v6 : '',
        title: LangFranchiseeEngineeredToOrder.newSpecialItem(),
        virtualThumbnailPath: '',
        commentToManufacturer: null,
        isShippingItem: false,

        state: QuoteItemState.Active
      },
      price: {
        breakdowns: null,
        calculatedGrossSellingPrice: 0,
        calculatedNetSellingPrice: 0,
        dateCreated: transactionDate,
        id: this.dataPrimaryKey,
        isTaxableItem: true,
        percentMarginOrMarkup: null,
        priceAdjustment: 0,
        quantityCost: 0,
        recordVersion: '',
        requiresRepricing: false,
        singleUnitCost: 0,
        sourceData: {},
        supplierGrossQuantityCost: 0,
        supplierGrossSingleUnitCost: 0,
        supplierNettQuantityCost: 0,
        supplierNettSingleUnitCost: 0,
        supplierPriceAdjustment: 0,
        supplierPriceAdjustmentEnabled: false,
        supplierPriceAdjustmentReason: null
      },
      buyInData: undefined,
      data: this.usingV6
        ? {
            id: this.dataPrimaryKey,
            dateCreated: transactionDate,
            lastModifiedDate: transactionDate,
            lastModifiedUserId: emptyGuid,
            providerData: providerData ?? null,
            recordVersion: '',
            assetVirtualPaths: {}
          }
        : null
    };
    this.createMockBranchQuoteSupport(transactionDate);
  }
  private createMockBranchQuoteSupport(transactionDate: string) {
    if (!this.quoteItemContainer) throw new DevelopmentError('ETO QuoteItemContainer cannot be null');

    // Seed a draft in the SupportTicketService with forced IDs matching the quote item id (ticket + conversation id)
    const svc = this.qcm.supportTicketService;

    // Create draft session using forced ids for ticket + conversation entry
    this._draftTicketId = svc.createSupportTicketDraft({
      quoteId: this.quoteId,
      supportType: BranchQuoteSupportItemType.EngineeredToOrder,
      supplierName: this.qcm.supplierName,
      branchQuoteSupportId: this.dataPrimaryKey,
      conversationEntryId: this.dataPrimaryKey,
      masterDocumentConversationId: this.masterDocumentConversationId
    });
    svc.updateDraftSession(this._draftTicketId, {
      subject: this.quoteItemContainer.item.title,
      text: ''
    });

    this.quoteItemConversationLink = {
      dateCreated: transactionDate,
      id: newGuid(),
      recordVersion: '',
      conversationId: this.quoteItemContainer.item.id,
      quoteItemId: this.quoteItemContainer.item.id,
      quoteId: this.quoteManager.quote.id,
      type: QuoteItemConversationType.EngineeredToOrder
    };
  }

  public async saveQuoteItem(): Promise<boolean> {
    await this.save();
    return true;
  }

  public async _internalSaveQuoteItemAfterSupport(
    hasStatusChange: boolean,
    newStatus: BranchQuoteSupportStatus
  ): Promise<boolean> {
    const qm = this.quoteManager;
    if (!this.quoteItemContainer) return false;
    await this.setInfo(LangFranchiseeEngineeredToOrder.requestingUpdatesFromSupplier());
    let requiresSSI = false;
    const deleting = hasStatusChange && flagEquals(newStatus, BranchQuoteSupportStatus.Cancelled);
    const excludeFromSSI = deleting ? [this.quoteItemContainer.item.id] : [];

    const ssiProcessor = qm.ssiProcessor(excludeFromSSI);
    if (!this.usingV6) {
      if (this.priceOrQtyChanged) {
        if (
          !(await updateFreehandPrice(
            this.qcm.quote.supplierId,
            this.qcm.quote.quoteOwnerId,
            this.quoteItemContainer.item,
            this.quoteItemContainer.price
          ))
        ) {
          await information(LangFranchiseeEngineeredToOrder.saveCancelledCouldNotCommunicateWithServer());
          return false;
        }
        requiresSSI = true;
      }
    } else {
      if (!this.quoteManager) throw new DevelopmentError('FranchiseeQuoteContainerManager must be assigned');
      if (this.usingV6 && this.v6Editor && this.quoteItemContainer) {
        //v6 data binding moved into a presave event.
        requiresSSI = this._v6CommitDetails !== null;
        // Ensure function returns boolean when using V6 path and no early returns triggered.
      }
    }

    const undeleting = hasStatusChange && flagEquals(newStatus, BranchQuoteSupportStatus.ReActivated);

    if (undeleting) {
      requiresSSI = true;
      this.qcm.prepareForUnDelete(this.quoteItemContainer.item.id);
      //we need to undelete the item, and make sure SSI incorporates it
    }
    const ssiInputs = requiresSSI ? await ssiProcessor.processSSI() : undefined;
    await this.setInfo(LangFranchiseeEngineeredToOrder.supplierProcessingCompleteSendingUpdatesToServer());
    if (this.isNew()) {
      if (!(await this.createNewItem(ssiInputs))) return false;
    } else if (deleting) {
      if (!(await this.deleteExistingItem(ssiInputs))) return false;
    } else if (undeleting) {
      if (!(await this.undeleteExistingItem(ssiInputs))) return false;
    } else {
      if (!(await this.saveExistingItem(ssiInputs))) return false;
    }

    // apply any deferred group assignment once the item is persisted. This funnel handles the
    // status-change save paths (resolve, reactivate) that the editor's own save() post-step does
    // not reach. Skip it when the item is being cancelled/soft-deleted - a deleted item should not
    // be moved into a group.
    if (!deleting && this.hasPendingGroupChange()) await this.applyPendingGroupAssignment();

    this.v6Editor?.resetEditorState();
    this.backupPriceDetails();
    return true;
  }

  private _v6CommitDetails: V6QuoteFrameEditorData | null = null;
  private _triggerItemChangeEvent = false;

  /**
   * Stage 1: Commit V6 editor data and apply prices/frame data to the quote item container.
   * This extracts data from the V6 editor without triggering conversation/support ticket updates.
   */
  private async commitV6Data(): Promise<void> {
    if (this.usingV6 && this.v6Editor && this.quoteItemContainer) {
      if (!this.quoteItemContainer.data)
        throw new DevelopmentError('EngineeredToOrderLineItemView, quoteItemContainer.data is null');
      this._v6CommitDetails = null;
      try {
        await this.v6BusyCheck();
        if (
          this.isNew() ||
          this.v6Editor.changed ||
          this.quoteManager.changedItemQuantity(this.quoteItemContainer?.item.id)
        ) {
          this._v6CommitDetails = await this.v6Editor.commit();
        }
      } catch {
        await offline();
      }
      this._triggerItemChangeEvent = false;
      if (this._v6CommitDetails) {
        const assets = convertV6CommitDetails(this._v6CommitDetails);
        delete assets['thumbnail']; // Handled separately below
        this.assets = { ...this.assets, ...assets };
        const buyIn = this._v6CommitDetails.buyInItems;
        const buyInCosts = await this.quoteManager.getBuyInCosts(buyIn);
        const buyInMetaData = this.buyInDataJsonStr(buyInCosts as unknown[] | undefined);

        const originalFrameData = this.originalFrameData;
        const newFrameData = this._v6CommitDetails.quoteItem
          ? v6Util().getFrameData(this._v6CommitDetails.quoteItem)
          : null;

        const frameDiff = this.isNew() ? null : compareFrames(originalFrameData, newFrameData, { excludeHidden: true });
        this.quoteItemContainer.differenceReport = frameDiff;
        this.quoteItemContainer.item.isRestrictedToPowerUser =
          this._v6CommitDetails.requiredUserMode === v6FrameUserMode.powerUser;
        applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections(
          this.quoteItemContainer.item.quantity,
          this._v6CommitDetails.price,
          this.quoteItemContainer.price,
          buyInCosts
        );
        setProviderData(this.quoteItemContainer, this.assets['data']?.data ?? null);
        this.quoteItemContainer.buyInData = buyInMetaData ?? undefined;
      }
    }
  }

  /**
   * Stage 2: After committing V6 data, handle conversation entries and support ticket updates.
   */
  private async bindV6Data(): Promise<void> {
    // Stage 1: Commit V6 editor data
    await this.commitV6Data();

    // Stage 2: Handle conversation/support ticket updates
    if (this._v6CommitDetails && this.quoteItemContainer) {
      const frameDiff = this.quoteItemContainer.differenceReport as FrameCompareReport | null;
      const markDown = getFrameCompareAsMarkdown('', frameDiff);
      if (!isEmptyOrSpace(markDown)) {
        this._triggerItemChangeEvent = true;
        const svc: SupportTicketService = this.qcm.supportTicketService;
        svc.addPendingConversationEntry({
          branchQuoteSupportId: this.branchQuoteSupportId,
          conversationEntryId: newGuid(),

          text: LangFranchiseeQuoteManager.quoteItemAttachToSupportRequestModifed(markDown, 'eto'),
          draft: false,
          users: [],
          attachments: []
        });
      }

      await this.qcm.needsQuoteSupport();
      await this.qcm.addConversationToLinkedSupportTickets(this.quoteItemContainer, {
        event: qic => {
          //todo, if we ever support other difference data refactor into method
          const markDown = isSupplierFrameItem(qic.item)
            ? getFrameCompareAsMarkdown(
                this.qcm.makeSupportLinkDisplayValueForQuoteItemId(qic.item.id, false),
                qic.differenceReport as FrameCompareReport
              )
            : '';
          return isEmptyOrSpace(markDown)
            ? []
            : [
                {
                  attachments: [],
                  id: newGuid(),
                  text: LangFranchiseeQuoteManager.quoteItemAttachToSupportRequestModifed(markDown),
                  users: [],
                  draft: false
                }
              ];
        },
        other: qic => {
          const itemlink = this.qcm.quoteItemLinkFor(qic.item.id);
          return this.qcm.branchQuoteSupport.items.filter(x =>
            itemlink.some(l => l.conversationId === x.conversationId)
          );
        }
      });
    }
  }
  // -
  // --------------- ETO Ticket Lifecycle Stub Hooks ----------------
  private _etoHooksRegistered = false;
  private _boundBeforeCreate?: (ctx: MutableSupportTicketBeforeCreateContext) => Promise<void>;
  private _boundPostCreate?: (ticket: BranchQuoteSupport, createCtx: SupportTicketCreateContext) => Promise<void>;
  private _boundBeforeEdit?: (ctx: MutableSupportTicketBeforeEditContext) => Promise<boolean>;
  private _boundPostEdit?: (ctx: SupportTicketPostEditContext) => Promise<void>;
  private _boundNeedsSaveFallback?: (type: BranchQuoteSupportItemType, ticketId: string | null) => boolean | undefined;

  private async _handleBeforeCreate(_ctx: MutableSupportTicketBeforeCreateContext): Promise<void> {
    await this.bindV6Data();
  }

  private async _handlePostCreate(ticket: BranchQuoteSupport, _createCtx: SupportTicketCreateContext): Promise<void> {
    try {
      await this._internalSaveQuoteItemAfterSupport(false, ticket.status);
      // Removed redundant openTicket invocation: saveTicket already opens the ticket and loads conversation.
      this.quoteItemConversationLink = await getApiFactory().franchisee().quoteItemConversationLink({
        quoteId: this.qcm.quoteId,
        conversationId: this.quoteItemContainer!.item.id,
        quoteItemId: this.quoteItemContainer!.item.id,
        type: QuoteItemConversationType.EngineeredToOrder
      });

      if (this._triggerItemChangeEvent)
        fireQuickInformationToast(LangFranchiseeQuoteManager.supportRequestsUpdatedDueToItemChange(1, 'eto'), 5000);
    } finally {
      this._isNewItem = false;
    }
  }

  private async _handleBeforeEdit(_ctx: MutableSupportTicketBeforeEditContext): Promise<boolean> {
    await this.bindV6Data();

    // Return true if bindV6Data detected changes that require save
    return !!(this._v6CommitDetails || this._triggerItemChangeEvent);
  }

  private async _handlePostEdit(ctx: SupportTicketPostEditContext): Promise<void> {
    await this._internalSaveQuoteItemAfterSupport(
      ctx.hasStatusChange,
      ctx.hasStatusChange ? ctx.newStatus! : BranchQuoteSupportStatus.Draft
    );
    if (this._triggerItemChangeEvent)
      fireQuickInformationToast(LangFranchiseeQuoteManager.supportRequestsUpdatedDueToItemChange(1, 'eto'), 5000);
  }

  private _registerEtoTicketHooks() {
    if (this._etoHooksRegistered) return;
    const svc = this.qcm?.supportTicketService;
    if (!svc) return;

    // Create bound references once and store them
    this._boundBeforeCreate = this._handleBeforeCreate.bind(this);
    this._boundPostCreate = this._handlePostCreate.bind(this);
    this._boundBeforeEdit = this._handleBeforeEdit.bind(this);
    this._boundPostEdit = this._handlePostEdit.bind(this);
    this._boundNeedsSaveFallback = this._etoNeedsSaveFallback.bind(this);

    // Register using the stored bound references
    svc.registerBeforeCreate(this._boundBeforeCreate);
    svc.registerPostCreate(this._boundPostCreate);
    svc.registerBeforeEdit(this._boundBeforeEdit);
    svc.registerPostEdit(this._boundPostEdit);
    // Register fallback needsSave handler so ETO view can force save-needed when intrinsic ticket logic is idle.
    svc.registerNeedsSave(this._boundNeedsSaveFallback);

    this._etoHooksRegistered = true;
  }

  private _unregisterEtoTicketHooks() {
    if (!this._etoHooksRegistered) return;
    const svc = this.qcm?.supportTicketService;
    if (!svc) return;

    // Unregister using the exact same bound references
    if (this._boundBeforeCreate) svc.unregisterBeforeCreate(this._boundBeforeCreate);
    if (this._boundPostCreate) svc.unregisterPostCreate(this._boundPostCreate);
    if (this._boundBeforeEdit) svc.unregisterBeforeEdit(this._boundBeforeEdit);
    if (this._boundPostEdit) svc.unregisterPostEdit(this._boundPostEdit);
    if (this._boundNeedsSaveFallback) svc.unregisterNeedsSave(this._boundNeedsSaveFallback);

    // Clear the bound references
    this._boundBeforeCreate = undefined;
    this._boundPostCreate = undefined;
    this._boundBeforeEdit = undefined;
    this._boundPostEdit = undefined;
    this._boundNeedsSaveFallback = undefined;

    this._etoHooksRegistered = false;
  }

  // Fallback needsSave handler: invoked only when intrinsic SupportTicketService needsSave returned false.
  // Determines if ETO-specific unsaved changes exist that warrant enabling save actions.
  private _etoNeedsSaveFallback(type: BranchQuoteSupportItemType, ticketId: string | null): boolean | undefined {
    try {
      if (type !== BranchQuoteSupportItemType.EngineeredToOrder) return false;
      // Ensure we are evaluating the current ticket; if different, do not handle (undefined).
      if (ticketId && ticketId !== this.currentTicketId) return undefined;
      // If component still initializing or missing container, allow other fallbacks to decide.
      if (!this.quoteItemContainer) return undefined;
      // New draft always considered changed.
      if (this.isNew()) return true;
      const v6Changed = this.usingV6 && !!this.v6Editor?.changed;
      const thumbnailChanged =
        this.originalThumbnail !== this.quoteItemContainer.item.virtualThumbnailPath ||
        this.thumbNailEditor?.isValueChanged;
      const qtyOrPriceChanged = this.priceOrQtyChanged;
      const quoteItemChanged = this.quoteManager.changedItem(this.quoteItemContainer.item.id);
      const stagedDifference = this._triggerItemChangeEvent;
      if (v6Changed || thumbnailChanged || qtyOrPriceChanged || quoteItemChanged || stagedDifference) return true;
      // Explicitly handled: no changes; return false (not undefined) so iteration stops.
      return false;
    } catch (err) {
      this.logWarn('etoNeedsSaveFallback', err);
      return undefined; // allow other handlers to participate if any
    }
  }

  // Snapshot helper removed (master document conversation id resolved inline where needed)

  // Logging helpers (single implementation retained)
  private logWarn(ctx: string, err: unknown) {
    console.warn(`[ETO][WARN] ${ctx}`, err);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wm-engineeredtoorderlineitemview-merged': EngineeredToOrderLineItemViewMerged;
  }
}
